import { DateMetaData } from "burde";
import * as moment from "moment";
import { Moment } from "moment";
import { D3Selection } from "../draw-calendar";
import { showRedText } from "./helper";
import { colors, dayLetterElementIdentifiers } from "./constants";

const drawDynamicMonthModule = (
    weekModuleElement: D3Selection,
    startDate: Moment,
    metaData: DateMetaData[],
    culture: string | null,
    Header_WeekNumberPrefix: string,
) => {
    // The monday at the beginning of the first week that has days that are part of the month.
    let iterationDate = getIterationDate(startDate);
    const monthToDraw = getMonthToDraw(iterationDate);
    drawMonthName(weekModuleElement, monthToDraw);
    drawWeekNumbers(
        weekModuleElement,
        iterationDate,
        monthToDraw,
        Header_WeekNumberPrefix,
    );
    drawDayLetters(weekModuleElement, iterationDate);
    drawDayNumbers(
        weekModuleElement,
        iterationDate,
        monthToDraw,
        metaData,
        culture,
    );
};

/* 
The month to draw should be whatever month the startDate is, unless the next month starts
within the same week.
*/
const getMonthToDraw = (startDate: Moment) => {
    const currentMonth = startDate.clone().format("MM");
    const monthAtEndOfWeek = startDate
        .clone()
        .endOf("isoWeek")
        .add(1, "day")
        .format("MM");

    if (currentMonth === monthAtEndOfWeek) {
        return startDate.format("MM");
    }

    return monthAtEndOfWeek;
};
/*
The iteration date should be the start of the week when the starting month begins.
*/
const getIterationDate = (startDate: Moment) => {
    const dateAtEndOfWeek = startDate.clone().endOf("isoWeek");
    let iterationDate = startDate.clone();

    iterationDate = moment(
        dateAtEndOfWeek.clone().startOf("month").startOf("isoWeek"),
    );

    return iterationDate;
};

const drawMonthName = (weekModuleElement: D3Selection, monthToDraw: string) => {
    const monthNameContainer = weekModuleElement.select(".header");
    const monthNameElement = monthNameContainer.selectAll("text");

    const monthName = moment(monthToDraw, "MM").format("MMMM");
    const formattedMonthName =
        monthName.charAt(0).toUpperCase() + monthName.slice(1);

    monthNameElement.text(formattedMonthName);
};

const drawWeekNumbers = (
    weekModuleElement: D3Selection,
    iterationDate: Moment,
    monthToDraw: string,
    Header_WeekNumberPrefix: string,
) => {
    const weekNumberContainer = weekModuleElement.select(".weekNumbers");
    const weekElements = weekNumberContainer.selectAll("text");
    let weekDateCounter = iterationDate.clone();

    weekElements.each(function (d: unknown, i: number) {
        const weekElement = weekNumberContainer.select(
            `.weekLetterNumber${i + 1}`,
        );

        if (!weekBelongsToMonth(weekDateCounter, monthToDraw)) {
            weekElement.text("");
        } else {
            const week = weekDateCounter.isoWeek();
            weekElement.text(`${Header_WeekNumberPrefix}${week}`);
        }

        weekDateCounter = moment(
            weekDateCounter.clone().add(1, "week").format("YYYY-MM-DD"),
            "YYYY-MM-DD",
        );
    });
};

const weekBelongsToMonth = (iterationDate: Moment, monthToDraw: string) => {
    const iterationMonthAtStartOfWeek = iterationDate
        .clone()
        .startOf("isoWeek")
        .format("MM");
    const iterationMonthAtEndOfWeek = iterationDate
        .clone()
        .endOf("isoWeek")
        .format("MM");

    if (
        iterationMonthAtStartOfWeek !== monthToDraw &&
        iterationMonthAtEndOfWeek !== monthToDraw
    ) {
        return false;
    }

    return true;
};

const drawDayNumbers = (
    weekModuleElement: D3Selection,
    iterationDate: Moment,
    monthToDraw: string,
    metaData: DateMetaData[],
    culture: string | null,
) => {
    const dayNumberContainer = weekModuleElement.select(".dayNumbers");
    const dayElements = dayNumberContainer.selectAll("text");

    dayElements.each(function (d: unknown, i: number) {
        const dayElement = dayNumberContainer.select(`.dayNumber${i + 1}`);
        const iterationMonth = iterationDate.clone().format("MM");

        if (!weekBelongsToMonth(iterationDate, monthToDraw)) {
            dayElement.text("");
        } else {
            if (iterationMonth !== monthToDraw) {
                dayElement.attr("fill", "#a1a1a1");
            } else {
                const dateMetaData = metaData.find(
                    (d) => d.date === iterationDate.format("YYYYMMDD"),
                );

                if (showRedText(iterationDate, dateMetaData, culture)) {
                    let accentColor = colors.redDay;
                    dayElement.attr("fill", accentColor);
                } else {
                    dayElement.attr("fill", "#555");
                }
            }

            const dayNumber = iterationDate.clone().format("D");
            dayElement.text(`${dayNumber}`);
        }

        iterationDate = moment(
            iterationDate.clone().add(1, "days").format("YYYY-MM-DD"),
            "YYYY-MM-DD",
        );
    });
};

const drawDayLetters = (
    weekModuleElement: D3Selection,
    startOfWeek: Moment,
) => {
    let iterationDate = startOfWeek.clone();
    const dayNumberContainer = weekModuleElement.select(".dayLetters");

    dayLetterElementIdentifiers.forEach((className) => {
        const dayElement = dayNumberContainer.select(`.${className}`);
        dayElement.text(iterationDate.format("dddd").charAt(0).toUpperCase());

        iterationDate = moment(
            iterationDate.add(1, "day").format("YYYY-MM-DD"),
            "YYYY-MM-DD",
        );
    });
};

export default drawDynamicMonthModule;
